/**
 * useFxInr — one-shot fetch of today's INR conversion rates.
 *
 * Cached in localStorage keyed by `as_of` date so a page refresh doesn't
 * re-hit the backend. The backend itself caches for a day, but this saves
 * the round-trip when the same visitor navigates across country dossiers.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "moe_fx_inr_v1";

// Module-level in-flight promise — dedupe simultaneous mounts.
let _inflight = null;

function _readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.as_of || !parsed?.inr_per) return null;
    return parsed;
  } catch { return null; }
}

function _writeCache(payload) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
}

function _todayUTC() {
  return new Date().toISOString().slice(0, 10);
}

export function useFxInr() {
  const [state, setState] = useState(() => {
    const c = _readCache();
    if (c && c.as_of === _todayUTC()) {
      return { rates: c.inr_per, asOf: c.as_of, ready: true, available: true, stale: false };
    }
    return { rates: null, asOf: null, ready: false, available: false, stale: false };
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (state.ready) return; // fresh cache already good

    const run = async () => {
      if (!_inflight) {
        _inflight = api.get("/fx/inr").then(r => r.data).catch(() => ({ available: false }));
      }
      const data = await _inflight;
      _inflight = null;
      if (!mounted.current) return;
      if (data?.available) {
        _writeCache({ as_of: data.as_of, inr_per: data.inr_per });
        setState({
          rates:     data.inr_per,
          asOf:      data.as_of,
          ready:     true,
          available: true,
          stale:     !!data.stale,
        });
      } else {
        setState(s => ({ ...s, ready: true, available: false }));
      }
    };
    run();
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
